import { Injectable, inject } from '@angular/core';
import { HttpService } from '@nha/api/shared';
import { catchError, map, Observable, of } from 'rxjs';
import { ReportingApiConfiguration } from '../reporting-api-configuration.class';
import { NHASnackbarService } from '@nha/components/snack-bar/snack-bar.service';
import { IReportDefinitionDetail } from '../types/report-definition-detail.type';
import { IReportDefinition } from '../types/report-definition.type';
import { IUpdateReportDefinitionRequest } from '../types/update-report-definition-request.type';
import { IReportCategory } from '../types/report-category.type';
import { IReportDefinitionParameter } from '../types/report-definition-parameter.type';
import { IReportDefinitionForSystemAdmin } from '../types/report-definition-for-system-admin.type';

@Injectable({
	providedIn: 'root'
})
export class ReportDefinitionService extends HttpService {
	constructor() {
		const config = inject(ReportingApiConfiguration);
		const snackbarService = inject(NHASnackbarService);

		super(config, 'ReportDefinition', snackbarService);
	}

	getByReportName(reportName: string): Observable<IReportDefinitionDetail> {
		return this.http.get<IReportDefinitionDetail>(`${this.serviceUrl}/reportName?reportName=${reportName}`);
	}

	getByCategory(categoryID: number): Observable<IReportDefinitionDetail[]> {
		return this.http.get<IReportDefinitionDetail[]>(`${this.serviceUrl}/category/${categoryID}`);
	}

	getForContext(contextName: string, includeParameterValues: boolean = true): Observable<IReportDefinitionDetail[]> {
		return this.http.get<IReportDefinitionDetail[]>(`${this.serviceUrl}/context/${contextName}/${includeParameterValues}`);
	}

	getAllSimpleReportDefinitions(): Observable<IReportDefinition[]> {
		return this.get<IReportDefinition[]>(`${this.serviceUrl}/user/simple`, { snackbarErrorMessage: 'Something went wrong while loading reports.' });
	}

	getReportDefinitionByID(reportDefinitionID: number): Observable<IReportDefinitionDetail> {
		return this.get<IReportDefinitionDetail>(`${this.serviceUrl}/detail/${reportDefinitionID}`, {
			snackbarErrorMessage: 'Something went wrong while loading the report parameters.'
		});
	}

	getReportDefinitionsForSystemAdmin(): Observable<IReportDefinitionForSystemAdmin[]> {
		return this.get<IReportDefinitionForSystemAdmin[]>(`${this.serviceUrl}/system-admin`, {
			snackbarErrorMessage: 'Something went wrong while loading reports.'
		});
	}

	updateReportDefinition(updateReportDefinitionRequest: IUpdateReportDefinitionRequest): Observable<IReportDefinitionForSystemAdmin[]> {
		return this.post<IReportDefinitionForSystemAdmin[]>(`${this.serviceUrl}/update`, updateReportDefinitionRequest, {
			snackbarErrorMessage: 'Something went wrong while editing the report.'
		}).pipe(
			map((response) => {
				this.snackbarService.success('The report has been edited successfully.');
				return response;
			})
		);
	}

	getAllReportCategories(): Observable<IReportCategory[]> {
		return this.http.get<IReportCategory[]>(`${this.serviceUrl}/categories`).pipe(
			map((response) => response),
			catchError((err) => {
				return of(err);
			})
		);
	}

	getReportDefinitionDetailByID(reportDefinitionID: number): Observable<IReportDefinitionDetail> {
		return this.get<IReportDefinitionDetail>(`${this.serviceUrl}/detail/${reportDefinitionID}`, {
			snackbarErrorMessage: 'Something went wrong while loading the report parameters.'
		});
	}

	getReportParameterUpdate(reportParameters: IReportDefinitionParameter[], reportName: string, stateID: number): Observable<IReportDefinitionParameter[]> {
		return this.http
			.post<IReportDefinitionParameter[]>(`${this.serviceUrl}/parameters/update`, { reportDefinitionParameters: reportParameters, reportName, stateID })
			.pipe(
				map((response) => response),
				catchError((err) => {
					this.snackbarService.error('Something went wrong while loading the report parameters.');
					return of(err);
				})
			);
	}

	addStaffFavorite(reportDefinitionID: number): Observable<IReportDefinition[]> {
		return this.post<IReportDefinition[]>(`${this.serviceUrl}/favorite/add/${reportDefinitionID}`, {
			snackbarErrorMessage: 'Something went wrong while adding the report to your favorites.'
		});
	}

	removeStaffFavorite(reportDefinitionID: number): Observable<IReportDefinition[]> {
		return this.delete<IReportDefinition[]>(`${this.serviceUrl}/favorite/remove/${reportDefinitionID}`, {
			snackbarErrorMessage: 'Something went wrong while removing the report to your favorites.'
		});
	}
}
